import {
  RefCommunicationTemplateCreateData,
  RefCommunicationTemplateListParams,
  RefCommunicationTemplatePageResponse,
} from "@/types/refCommunicationTemplate";
import { adminUrl, apiUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { IRefCommunicationTemplatesService } from "./IRefCommunicationTemplatesService";

export class RefCommunicationTemplatesService implements IRefCommunicationTemplatesService {
  private httpClient: HttpClient;
  /**
   * Writes go to `/api/admin/refCommunicationTemplates`; the plain
   * `/api/refCommunicationTemplates` only serves GET, so create, update and
   * delete need their own client.
   */
  private adminHttpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
    this.adminHttpClient = new HttpClient({
      baseURL: adminUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async getAll({
    page = 0,
    size = 100,
    isFollowUp,
    isExternal,
    communicationTypeId,
    signal,
  }: RefCommunicationTemplateListParams = {}): Promise<RefCommunicationTemplatePageResponse> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort: "id",
    });

    if (isFollowUp !== undefined) {
      queryParams.set("isFollowUp", String(isFollowUp));
    }
    if (isExternal !== undefined) {
      queryParams.set("isExternal", String(isExternal));
    }
    if (communicationTypeId !== undefined) {
      queryParams.set("communicationTypeId", String(communicationTypeId));
    }

    try {
      return await this.httpClient.get<RefCommunicationTemplatePageResponse>(
        `/refCommunicationTemplates?${queryParams.toString()}`,
        signal ? { signal } : {},
      );
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar os modelos de comunicação. Atualize a página e tente novamente.",
        ),
      );
    }
  }

  async create(
    templateData: RefCommunicationTemplateCreateData,
  ): Promise<number> {
    try {
      return await this.adminHttpClient.post<number>(
        `/refCommunicationTemplates`,
        templateData,
        {},
        true,
      );
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível criar o modelo de comunicação. Confirme os campos obrigatórios.",
        ),
      );
    }
  }

  async update(
    id: number,
    templateData: RefCommunicationTemplateCreateData,
  ): Promise<void> {
    try {
      await this.adminHttpClient.put<void>(
        `/refCommunicationTemplates/${id}`,
        templateData,
      );
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível atualizar o modelo de comunicação. Tente guardar novamente.",
        ),
      );
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.adminHttpClient.delete<void>(
        `/refCommunicationTemplates/${id}`,
      );
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível eliminar o modelo de comunicação. O modelo pode estar a ser usado por comunicações existentes.",
        ),
      );
    }
  }
}
